/**
 * HelpModal
 *
 * The "Get help" dialog from the account menu. The user writes a subject and
 * a message; the backend emails it to JAI's support inbox (via Resend) with
 * their address as Reply-To, and sends them a confirmation copy.
 *
 * Their name, email and account details are attached automatically on the
 * backend from their login — so there's nothing to fill in but the problem
 * itself, and nobody can send an enquiry pretending to be someone else.
 *
 * Props:
 *   onClose — close the dialog
 */

import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { supportApi } from '../services/api'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import ModalPortal, { ModalHeader, ModalFooter, GhostButton, SolidButton } from './ModalPortal'
import { Notice } from './JaiV2'

// Matches the backend's limits (supportController.js) so the user is told
// they've run long while typing, rather than after hitting Send.
const MAX_SUBJECT = 150
const MAX_MESSAGE = 5000

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: `1px solid ${JAI_COLORS.hair}`,
  background: JAI_COLORS.paper,
  fontFamily: JAI_FONTS.tight,
  fontSize: 14,
  color: JAI_COLORS.navy,
  outline: 'none',
  transition: 'border-color 150ms, background 150ms, box-shadow 150ms',
}

const focusOn = (e) => {
  e.target.style.borderColor = JAI_COLORS.blue
  e.target.style.background = 'white'
  e.target.style.boxShadow = `0 0 0 3px rgba(30,91,214,0.12)`
}

const focusOff = (e) => {
  e.target.style.borderColor = JAI_COLORS.hair
  e.target.style.background = JAI_COLORS.paper
  e.target.style.boxShadow = 'none'
}

function Label({ children, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: JAI_COLORS.navy }}>{children}</span>
      {hint && <span style={{ fontSize: 11.5, color: JAI_COLORS.mute }}>{hint}</span>}
    </div>
  )
}

export default function HelpModal({ onClose }) {
  const { user } = useAuth()

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const canSend = subject.trim() && message.trim() && !sending

  const handleSend = async (e) => {
    e.preventDefault()
    if (!canSend) return

    setSending(true)
    setError('')
    try {
      await supportApi.sendEnquiry(subject.trim(), message.trim())
      setSent(true)
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send your message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  // ── Sent confirmation ────────────────────────────────────────────────────
  if (sent) {
    return (
      <ModalPortal onClose={onClose} width={460} labelledBy="help-sent-title">
        <div style={{ padding: '34px 28px 28px', textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', margin: '0 auto 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${JAI_COLORS.success}`,
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M4 12.5l5 5L20 6.5" stroke={JAI_COLORS.success} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h2 id="help-sent-title" style={{ margin: '0 0 8px', fontFamily: JAI_FONTS.tight, fontSize: 18, fontWeight: 700, color: JAI_COLORS.navy }}>
            Message sent
          </h2>
          <p style={{ margin: 0, fontFamily: JAI_FONTS.tight, fontSize: 14, lineHeight: 1.6, color: JAI_COLORS.mute }}>
            Thanks for getting in touch — we'll reply to <strong style={{ color: JAI_COLORS.navy, fontWeight: 600 }}>{user?.email}</strong> within
            1–2 business days. A copy is on its way to your inbox now.
          </p>
        </div>

        <ModalFooter>
          <SolidButton onClick={onClose}>Done</SolidButton>
        </ModalFooter>
      </ModalPortal>
    )
  }

  // ── Enquiry form ─────────────────────────────────────────────────────────
  return (
    <ModalPortal onClose={onClose} closable={!sending} width={520} labelledBy="help-title">
      <ModalHeader title="Get help" titleId="help-title" onClose={onClose} closable={!sending} />

      {/* The form is the flex column here so its body scrolls while the
          footer buttons stay pinned to the bottom of the dialog. */}
      <form onSubmit={handleSend} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          <p style={{ margin: '0 0 20px', fontFamily: JAI_FONTS.tight, fontSize: 13.5, lineHeight: 1.6, color: JAI_COLORS.mute }}>
            Tell us what's going on and we'll get back to you at{' '}
            <strong style={{ color: JAI_COLORS.navy, fontWeight: 600 }}>{user?.email}</strong>.
            Your account details are included automatically.
          </p>

          {error && (
            <Notice tone="error" style={{ marginTop: 0 }}>{error}</Notice>
          )}

          <div style={{ marginBottom: 16 }}>
            <Label>Subject</Label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, MAX_SUBJECT))}
              onFocus={focusOn}
              onBlur={focusOff}
              disabled={sending}
              placeholder="What do you need help with?"
              autoFocus
              style={inputStyle}
            />
          </div>

          <div>
            <Label hint={`${message.length} / ${MAX_MESSAGE}`}>Message</Label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              onFocus={focusOn}
              onBlur={focusOff}
              disabled={sending}
              rows={7}
              placeholder="Give us as much detail as you can — what you were doing, what you expected, and what happened instead."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 120, lineHeight: 1.6 }}
            />
          </div>
        </div>

        <ModalFooter>
          <GhostButton onClick={onClose} disabled={sending}>Cancel</GhostButton>
          <SolidButton type="submit" disabled={!canSend} loading={sending}>
            {sending ? 'Sending…' : 'Send message'}
          </SolidButton>
        </ModalFooter>
      </form>
    </ModalPortal>
  )
}
